import { useLazyQuery } from '@apollo/client';
import { Box, ButtonAppearance, Icon, IconSize, Radio, Text, TextFieldSize, theme } from '@bechtel/gds';
import {
  Cable,
  ConstructionStatus,
  MetadataTable,
  MetadataTableName,
  OperatorType,
  UiTextEnum,
} from '@bechtel/sr-types';
import { FieldArray, useFormikContext } from 'formik';
import { filter, find, get, isEmpty, isNull, isUndefined, omit } from 'lodash';
import { FC, useEffect, useRef, useState } from 'react';
import {
  FormField,
  FormLabelSize,
  IconButtonStyled,
  InputWrapperStyled,
  LabelWrapper,
  MetadataText,
  MultiLingualText,
  PickListFilterContext,
  TableMetadataContext,
  TextFieldComponent,
  useLanguage,
} from '~/components';
import { GET_SELECT_LIST_DATA } from '~/gql';
import { useDebounce, usePermissions } from '~/hooks';
import { getCutLength } from '../../utils';
import { BoxStyled, ContainerBoxStyled, ContainerStyled, MarkContainerStyled, TextFieldStyled } from '../styled';
import { CableFeedbackFormikState } from '../types';

export const CableReels: FC<{
  cableReelTableMetadata: MetadataTable;
  loadingCheck: boolean;
  cableData: Cable;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setText: React.Dispatch<React.SetStateAction<string>>;
  setInitialValues: React.Dispatch<React.SetStateAction<CableFeedbackFormikState>>;
  initialValues: CableFeedbackFormikState;
}> = ({
  cableReelTableMetadata,
  loadingCheck,
  cableData,
  setIsModalOpen,
  setText,
  setInitialValues,
  initialValues,
}) => {
  const { setFieldValue, values } = useFormikContext<CableFeedbackFormikState>();
  const columnMetadata = find(cableReelTableMetadata?.metadataColumns, (item) => item.memberName === 'reelId');
  const { getMultiLingualTextByKey } = useLanguage();
  const { canUpdateCableFeedback } = usePermissions();
  const [isMarkChecked, setIsMarkChecked] = useState({});
  const [isMarked, setIsMarked] = useState({});
  const cutLengthColumn = find(cableReelTableMetadata?.metadataColumns, { memberName: 'cutLength' });
  const metadataColumn = find(cableReelTableMetadata?.metadataColumns, (item) => item.memberName == 'reelId');
  const indexRef = useRef<number>(-1);
  const reelPath = `cableReels[${indexRef.current}].reel.tag`;
  const reelTag = get(values, reelPath);
  const debouncedReelTag = useDebounce(reelTag, 500);
  const [getSelectListData, { data }] = useLazyQuery(GET_SELECT_LIST_DATA, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    // This action functions as an onChange event for the reel tag.
    // If the returned value is null, it disables the save button.
    if (debouncedReelTag) {
      const filter = {
        and: [
          {
            field: 'TAG',
            operator: OperatorType.EQUAL,
            value: debouncedReelTag,
          },
        ],
      };
      getSelectListData({
        variables: {
          name: metadataColumn?.pickListName,
          searchInput: {
            filterInput: filter,
          },
        },
        onCompleted: (data) => {
          const selectListDataItems = get(data, 'getSelectListData.items', null);
          if (isNull(selectListDataItems)) {
            setFieldValue(`cableReels[${indexRef.current}].reel.id`, '');
          } else {
            setFieldValue(`cableReels[${indexRef.current}].reel.id`, selectListDataItems[0]['column1']);
          }
        },
      });
    }
  }, [debouncedReelTag, getSelectListData, metadataColumn?.pickListName, setFieldValue]);

  const handleSelect = (type: string, index: number) => {
    const diffType = type === 'startMark' ? 'cutLength' : 'startMark';
    return setIsMarkChecked((prev) => ({
      ...prev,
      [`${type}-${index}`]: true,
      [`${diffType}-${index}`]: false,
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, key: string, index: number) => {
    setIsMarked((prev) => ({
      ...prev,
      [`${key}-${index}`]: e.target.value,
    }));
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>, key: string, index: number) => {
    const decimalUpto = cutLengthColumn?.decimals ?? 0;
    const value = isEmpty(e.target.value) ? '' : parseFloat(e.target.value)?.toFixed(decimalUpto);
    setIsMarked((prev) => ({
      ...prev,
      [`${key}-${index}`]: value,
    }));

    const cutLength = getCutLength(index, decimalUpto, isMarked);
    if (!isUndefined(cutLength?.value)) {
      setFieldValue(cutLength?.key, cutLength?.value);
      setIsMarkChecked({ [`cutLength-${index}`]: true });
    }

    if (!isEmpty(isMarked[`stopMark-${index}`]) && !isEmpty(isMarked[`startMark-${index}`])) {
      setIsMarked({ [`stopMark-${index}`]: '', [`startMark-${index}`]: '' });
    }
  };

  const pickListFilter = {
    pickListName: columnMetadata?.pickListName,
    filter: {
      and: [
        {
          field: 'CABLE_CODE_ID',
          operator: OperatorType.EQUAL,
          value: cableData?.cableCode?.id,
        },
      ],
    },
  };

  const handleReelBlur = (e) => {
    const reelTag = get(values, e.target.name);
    if (reelTag) {
      if (isNull(get(data, 'getSelectListData.items', null)) && metadataColumn?.columnPrompt) {
        setIsModalOpen(true);
        setText(reelTag);
      }
    }
  };

  return (
    <>
      <TableMetadataContext metadataTableName={MetadataTableName.CABLE_REEL}>
        <PickListFilterContext pickListFilter={pickListFilter}>
          <FieldArray
            name="cableReels"
            render={(arrayHelpers) => (
              <>
                <Box mb={{ sm: '400' }} justifyContent={{ sm: 'space-between' }} flexDirection={{ sm: 'column' }}>
                  {values?.cableReels?.map((item, index) => {
                    const matchingData = !isUndefined(get(item, 'reel.badLength'));
                    indexRef.current = index;
                    return (
                      <Box key={index} mb={{ sm: '450' }}>
                        <ContainerStyled>
                          <Box mb={{ sm: '600' }} css={{ gap: '10px' }} flexDirection={{ sm: 'row' }}>
                            <Box style={{ width: '50%' }}>
                              <FormField
                                alwaysEditable={!(matchingData || loadingCheck) && canUpdateCableFeedback}
                                memberName="reelId"
                                displayMemberId={`cableReels[${index}].reel.id`}
                                displayMemberName={`cableReels[${index}].reel.tag`}
                                showPicker={!matchingData}
                                hasFieldPicklist={true}
                                required={values.constructionStatus === ConstructionStatus.CABLE_CUT}
                                isMainCommodity={true}
                                commodityName={
                                  get(values, `cableReels[${index}].reel.id`)
                                    ? `commodities/reel`
                                    : `commodities/reel/new`
                                }
                                validate={false}
                                onParentBlur={(e) => handleReelBlur(e)}
                              >
                                <TextFieldComponent />
                              </FormField>
                            </Box>
                            <Box ml={{ sm: '550' }} mr={{ sm: '400' }}>
                              <ContainerBoxStyled>
                                <BoxStyled>
                                  <Box mr={{ sm: '450' }}>
                                    <MetadataText metadataTable={cableReelTableMetadata} memberName="assignedLength" />
                                  </Box>
                                  <Text size="300" weight="600" color="black">
                                    {item?.assignedLength}
                                  </Text>
                                </BoxStyled>
                              </ContainerBoxStyled>
                            </Box>
                          </Box>
                          <MultiLingualText color="grey.800" size="400">
                            Enter the length of cable cut from this reel
                          </MultiLingualText>
                          <Box mt={{ sm: '300' }}>
                            <Box flexDirection={{ sm: 'column' }}>
                              <Box
                                key={`startMark-${index}`}
                                alignItems={{ sm: 'center' }}
                                mb={{ sm: '450' }}
                                onClick={() => handleSelect('startMark', index)}
                              >
                                <Radio
                                  name={`startMark-${index}`}
                                  size={TextFieldSize.MEDIUM}
                                  id={`startMark-${index}`}
                                  checked={isMarkChecked[`startMark-${index}`]}
                                  readOnly={true}
                                  disabled={loadingCheck}
                                />
                                <MarkContainerStyled css={{ width: '344px' }} flexShrink={{ sm: '0' }}>
                                  <Box>
                                    <Box mr={{ sm: '100' }}>
                                      <InputWrapperStyled
                                        disabled={!isMarkChecked[`startMark-${index}`] || loadingCheck}
                                      >
                                        <LabelWrapper size={FormLabelSize.XSMALL}>
                                          <MultiLingualText color="grey.900" size="300">
                                            Start Mark
                                          </MultiLingualText>
                                        </LabelWrapper>
                                        <TextFieldStyled
                                          disabled={!isMarkChecked[`startMark-${index}`] || loadingCheck}
                                          onBlur={(e) => handleBlur(e, 'startMark', index)}
                                          onChange={(e) => handleChange(e, 'startMark', index)}
                                          changed={isMarked[`startMark-${index}`]}
                                          value={isMarked[`startMark-${index}`]}
                                          type="number"
                                          min={`${cutLengthColumn?.minValue}`}
                                          max={`${cutLengthColumn?.maxValue}`}
                                        />
                                      </InputWrapperStyled>
                                    </Box>
                                    <Box ml={{ sm: '200' }}>
                                      <InputWrapperStyled
                                        disabled={!isMarkChecked[`startMark-${index}`] || loadingCheck}
                                      >
                                        <LabelWrapper size={FormLabelSize.XSMALL}>
                                          <MultiLingualText color="grey.900" size="300">
                                            Stop Mark
                                          </MultiLingualText>
                                        </LabelWrapper>
                                        <TextFieldStyled
                                          changed={isMarked[`stopMark-${index}`]}
                                          disabled={!isMarkChecked[`startMark-${index}`] || loadingCheck}
                                          onBlur={(e) => handleBlur(e, 'stopMark', index)}
                                          onChange={(e) => handleChange(e, 'stopMark', index)}
                                          value={isMarked[`stopMark-${index}`]}
                                          type="number"
                                          min={`${cutLengthColumn?.minValue}`}
                                          max={`${cutLengthColumn?.maxValue}`}
                                        />
                                      </InputWrapperStyled>
                                    </Box>
                                  </Box>
                                </MarkContainerStyled>
                                <Box flexShrink={{ sm: 16 }} pr={{ sm: '450' }}>
                                  <Box mr={{ sm: '200' }}>
                                    <Icon
                                      icon="circle-info"
                                      iconStyle="regular"
                                      size={IconSize.SMALL}
                                      fillColor="red.500"
                                    />
                                  </Box>
                                  <MultiLingualText
                                    color="red.500"
                                    size="300"
                                    style={{ lineHeight: '15px', wordWrap: 'break-word' }}
                                  >
                                    To calculate the cut length, enter the start and stop length marks printed on the
                                    cable.
                                  </MultiLingualText>
                                </Box>
                              </Box>
                              <Box
                                alignItems={{ sm: 'center' }}
                                onClick={() => handleSelect('cutLength', index)}
                                key={`cutLength-${index}`}
                              >
                                <Radio
                                  name={`cutLength-${index}`}
                                  size={TextFieldSize.MEDIUM}
                                  id={`cutLength-${index}`}
                                  checked={
                                    isUndefined(isMarkChecked[`cutLength-${index}`])
                                      ? true
                                      : isMarkChecked[`cutLength-${index}`]
                                  }
                                  readOnly={true}
                                  disabled={loadingCheck}
                                />
                                <Box
                                  ml={{ sm: '450' }}
                                  mr={{ sm: '400' }}
                                  flexShrink={{ sm: '0' }}
                                  css={{ width: '344px' }}
                                >
                                  <FormField
                                    alwaysEditable={canUpdateCableFeedback}
                                    memberName="cutLength"
                                    displayMemberName={`cableReels[${index}].cutLength`}
                                    size={FormLabelSize.SMALL}
                                    readOnly={true}
                                    disabled={
                                      (isUndefined(isMarkChecked[`cutLength-${index}`])
                                        ? false
                                        : !isMarkChecked[`cutLength-${index}`]) || loadingCheck
                                    }
                                    required={values.constructionStatus === ConstructionStatus.CABLE_CUT}
                                  >
                                    <TextFieldComponent />
                                  </FormField>
                                </Box>
                                {cableData?.numberOfCables > 1 &&
                                  (isUndefined(isMarkChecked[`cutLength-${index}`])
                                    ? true
                                    : isMarkChecked[`cutLength-${index}`]) && (
                                    <Box flexShrink={{ sm: 16 }} pr={{ sm: '450' }}>
                                      <Box mr={{ sm: '200' }}>
                                        <Icon icon="triangle-exclamation" iconStyle="duo" fillColor="yellow.700" />
                                      </Box>
                                      <Text color="red.500" size="300" style={{ lineHeight: '15px' }}>
                                        {getMultiLingualTextByKey(UiTextEnum.NUMBER_OF_CABLES_IS_GREATER_THAN_ONE, [
                                          `${cableData?.numberOfCables}`,
                                        ])}
                                      </Text>
                                    </Box>
                                  )}
                              </Box>
                            </Box>
                          </Box>
                          <IconButtonStyled
                            style={{ background: theme.colors.grey[100], marginLeft: 'auto' }}
                            onClick={() => {
                              if (
                                typeof initialValues?.cableReels?.length == 'number' &&
                                initialValues?.cableReels?.length - 1 > index
                              ) {
                                const filteredCableReels = filter(initialValues?.cableReels, (_, idx) => idx !== index);
                                setInitialValues({ ...initialValues, cableReels: filteredCableReels });
                              }
                              omit(values, [`cableReels[${index}]`]);
                              arrayHelpers.remove(index);
                            }}
                            disabled={loadingCheck}
                          >
                            <Icon icon="trash-can" iconStyle="regular" fillColor="grey.800" size={IconSize.XSMALL} />
                          </IconButtonStyled>
                        </ContainerStyled>
                      </Box>
                    );
                  })}
                  <Box mr={{ sm: '200' }} flexDirection={{ sm: 'row' }}>
                    <IconButtonStyled
                      disabled={loadingCheck}
                      onClick={() => {
                        setIsMarked({});
                        arrayHelpers.push('');
                      }}
                      style={{ width: '100px' }}
                      appearance={ButtonAppearance.SECONDARY}
                    >
                      <Icon icon="plus" fillColor="black" iconStyle="regular" size={IconSize.XSMALL} />
                      <MultiLingualText>Add New</MultiLingualText>
                    </IconButtonStyled>
                  </Box>
                </Box>
              </>
            )}
          />
        </PickListFilterContext>
      </TableMetadataContext>
    </>
  );
};
